import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  listDigitalCards,
  selectDigitalCards,
  selectDigitalCardsError,
  selectDigitalCardsLoading,
  selectDigitalCardsMessage,
  selectDigitalCardsUpdateError,
  selectDigitalCardsUpdateMessage,
  setCardActiveOptimistic,
  updateDigitalCardActive,
} from "../digitalCardsSlice";
import { listUsers, selectUsers } from "../../user/userSlice";

const PAGE_SIZE = 10;

const DigitalCardsActivePage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const cards = useSelector(selectDigitalCards);
  const adminAccess = useSelector((state) => state.auth?.adminAccess);
  const isLoading = useSelector(selectDigitalCardsLoading);
  const isError = useSelector(selectDigitalCardsError);
  const message = useSelector(selectDigitalCardsMessage);
  const updateError = useSelector(selectDigitalCardsUpdateError);
  const updateMessage = useSelector(selectDigitalCardsUpdateMessage);
  const [permissionError, setPermissionError] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    dispatch(listDigitalCards());
    dispatch(listUsers());
  }, [dispatch]);

  const users = useSelector(selectUsers);

  const userMap = useMemo(() => {
    const map = new Map();
    (users || []).forEach((u) => {
      const id = u.id || u.userId;
      if (!id) return;
      const fullName = u.fullName || [u.name, u.surName].filter(Boolean).join(" ") || "";
      map.set(String(id), { name: fullName || "-", email: u.email || u.userEmail || "-" });
    });
    return map;
  }, [users]);

  const getOwnerId = (card) => {
    return (
      card?.userId ||
      card?.activateUserId ||
      (Array.isArray(card?.adminUsers) ? card.adminUsers[0] : undefined)
    );
  };

  const getName = (card) => {
    const ownerId = getOwnerId(card);
    if (ownerId && userMap.has(String(ownerId))) return userMap.get(String(ownerId)).name;
    return card?.fullName || card?.name || "-";
  };

  const highlightMatch = (text, term) => {
    if (!term) return text;
    const safeText = text || "";
    const lower = safeText.toLowerCase();
    const lowerTerm = term.toLowerCase();
    const index = lower.indexOf(lowerTerm);
    if (index === -1) return safeText || "-";
    const before = safeText.slice(0, index);
    const match = safeText.slice(index, index + term.length);
    const after = safeText.slice(index + term.length);
    return (
      <>
        {before}
        <span style={{ backgroundColor: "yellow" }}>{match}</span>
        {after}
      </>
    );
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return cards || [];
    return (cards || []).filter((c) => getName(c).toLowerCase().includes(term));
  }, [cards, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, currentPage]);

  useEffect(() => {
    if (page !== currentPage) setPage(currentPage);
  }, [page, currentPage]);

  const renderRows = () => {
    if (isLoading && (!cards || cards.length === 0)) {
      return (
        <tr>
          <td colSpan="3">{t("admin_common.loading")}</td>
        </tr>
      );
    }
    if (isError && (!cards || cards.length === 0)) {
      return (
        <tr>
          <td colSpan="3" className="text-danger">
            {message || t("admin_common.error_generic")}
          </td>
        </tr>
      );
    }
    if (!paged || paged.length === 0) {
      return (
        <tr>
          <td colSpan="3">{t("admin_common.no_records")}</td>
        </tr>
      );
    }
    return paged.map((c) => (
      <tr key={c.id}>
        <td>{highlightMatch(getName(c), search.trim())}</td>
        <td>{(userMap.get(String(getOwnerId(c)))?.email) || c.email || "-"}</td>
        <td>
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              id={`card-active-${c.id}`}
              checked={Boolean(c.isActive)}
              onChange={() => {
                if (!adminAccess) {
                  setPermissionError(t("admin_common.permission_denied"));
                  return;
                }
                setPermissionError("");
                const nextValue = !c.isActive;
                dispatch(
                  setCardActiveOptimistic({
                    id: c.id,
                    isActive: nextValue,
                    updatedAt: Date.now(),
                  })
                );
                dispatch(
                  updateDigitalCardActive({
                    id: c.id,
                    isActive: nextValue,
                    prevIsActive: c.isActive,
                  })
                );
              }}
            />
            <label className="form-check-label" htmlFor={`card-active-${c.id}`}>
              {c.isActive ? t("admin_common.status_active") : t("admin_common.status_passive")}
            </label>
          </div>
        </td>
      </tr>
    ));
  };

  return (
    <div className="row">
      <div className="col-12">
        <div className="card">
          <div className="card-body">
            <h4 className="card-title">{t("admin_digital_cards_pages.active_title")}</h4>
            {permissionError && (
              <div className="alert alert-danger">{permissionError}</div>
            )}
            {updateError && (
              <div className="text-danger mb-2">
                {updateMessage || t("admin_digital_cards_pages.update_failed")}
              </div>
            )}
            <div className="row mb-3">
              <div className="col-md-6">
                <input
                  type="text"
                  className="form-control"
                  placeholder={t("admin_common.search_name_placeholder")}
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
            </div>
            <div className="table-responsive">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>{t("admin_common.name")}</th>
                    <th>{t("admin_common.email")}</th>
                    <th>{t("admin_common.status")}</th>
                  </tr>
                </thead>
                <tbody>{renderRows()}</tbody>
              </table>
            </div>
            <div className="d-flex align-items-center justify-content-between mt-3">
              <span className="text-muted">
                {t("admin_common.total_records", { count: filtered.length })}
              </span>
              <div className="btn-group">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                >
                  {t("admin_common.prev")}
                </button>
                <button type="button" className="btn btn-outline-secondary" disabled>
                  {currentPage} / {totalPages}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                >
                  {t("admin_common.next")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DigitalCardsActivePage;
